import {
  Box,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useMemo, useState } from 'react';
import { isValidSharePointUrl, sanitizeInput } from '../../utils/formUtils';
import CopyButton from '../common/CopyButton';

import WizardNavigation from './WizardNavigation';

interface Props {
  onNext: (data: { siteId: string }) => void;
  onBack: () => void;
  activeStep: number;
  clientId: string;
  siteUrl: string;
  credentials: {
    siteId: string;
  };
}

const Step5SiteId: React.FC<Props> = ({
  onNext,
  onBack,
  activeStep,
  clientId,
  siteUrl,
  credentials,
}) => {
  const [siteId, setSiteId] = useState(credentials.siteId || '');
  const [siteIdError, setSiteIdError] = useState('');

  const handleSiteIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setSiteId(sanitizedValue);
    if (siteIdError) {
      validateSiteId(sanitizedValue);
    }
  };

  const validateSiteId = (value: string) => {
    if (!value) {
      setSiteIdError('Site ID is required.');
      return false;
    }
    if (value.length < 50) {
      setSiteIdError('Please enter a valid Site ID. It is usually a long string of text.');
      return false;
    }
    setSiteIdError('');
    return true;
  };

  const handleNext = () => {
    if (validateSiteId(siteId)) {
      onNext({ siteId });
    }
  };

  const isValidUrl = useMemo(() => isValidSharePointUrl(siteUrl), [siteUrl]);
  const appDomain = useMemo(() => {
    if (!isValidUrl) {
      return 'YOURCOMPANY.sharepoint.com';
    }
    return new URL(siteUrl).hostname;
  }, [siteUrl, isValidUrl]);

  const getAppInvUrl = () => {
    if (!isValidUrl) {
      return 'https://YOURCOMPANY.sharepoint.com/sites/YourSharePointSite/_layouts/15/appinv.aspx';
    }
    const url = new URL(siteUrl);
    return `${url.origin}${url.pathname}/_layouts/15/appinv.aspx`;
  };

  const getGraphExplorerUrl = () => {
    if (!isValidUrl) {
      return 'https://graph.microsoft.com/v1.0/sites/{hostname}:/sites/{site-path}';
    }
    const url = new URL(siteUrl);
    return `https://graph.microsoft.com/v1.0/sites/${url.hostname}:${url.pathname}`;
  };

  const permissionRequestXml = `<AppPermissionRequests AllowAppOnlyPolicy="true">
<AppPermissionRequest Scope="http://sharepoint/content/sitecollection/web" Right="Write" />
</AppPermissionRequests>`;

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        Step 5: Grant BIDLOGIQ Access to Your SharePoint Site & Get the Site ID
      </Typography>

      <Typography variant="h6" paragraph sx={{ textAlign: 'left', fontSize: '1.1rem', mt: 2 }}>
        <b>CRITICAL STEP:</b> You must grant your BIDLOGIQ app permission to access your SharePoint
        site.
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary={<b>1. Go to the App Permissions Page by navigating to the URL below:</b>}
          />
        </ListItem>
        <ListItem>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              bgcolor: 'greyContrast',
              color: 'primary.contrastText',
              borderRadius: 1,
              border: '1px solid #ddd',
              p: 1,
              pl: 2,
            }}
          >
            <Box
              component="code"
              sx={{ fontSize: '0.9rem', display: 'block', wordBreak: 'break-all' }}
            >
              {getAppInvUrl()}
            </Box>
            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
              <CopyButton textToCopy={getAppInvUrl()} />
            </Box>
          </Box>
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                <b>2. Enter Your Client ID:</b> In the "App Id" field, paste your Client ID from
                Step 3 and click "Lookup".
              </span>
            }
          />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <TextField
            label="Your Client ID"
            value={clientId}
            fullWidth
            variant="filled"
            margin="dense"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CopyButton textToCopy={clientId} />
                </InputAdornment>
              ),
            }}
            helperText="Copied from the previous step for your convenience."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                <b>If prompted for "App Domain", enter the following:</b>
              </span>
            }
          />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <TextField
            label="App Domain"
            value={appDomain}
            fullWidth
            variant="filled"
            margin="dense"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CopyButton textToCopy={appDomain} />
                </InputAdornment>
              ),
            }}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                <b>3. Grant Write Permission:</b> In the large text box labeled "Permission Request
                XML", copy and paste this exact text:
              </span>
            }
          />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              component="pre"
              sx={{
                bgcolor: 'greyContrast',
                color: 'primary.contrastText',
                padding: 1,
                borderRadius: 1,
                fontSize: '0.8rem',
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
              }}
            >
              {permissionRequestXml}
            </Box>
            <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton textToCopy={permissionRequestXml} />
            </Box>
          </Box>
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                <b>4. Complete the Process:</b> Click "Create" and then click "Trust It" to grant
                the permissions.
              </span>
            }
          />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Get the SharePoint Site ID
      </Typography>
      <Typography variant="body1" paragraph>
        This is the unique ID for the specific SharePoint site you want to access.
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary={
              <span>
                1. Go to the{' '}
                <Link
                  href="https://developer.microsoft.com/en-us/graph/graph-explorer"
                  target="_blank"
                  rel="noopener"
                >
                  Microsoft Graph Explorer
                </Link>{' '}
                and sign in.
              </span>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText primary="2. Verify you are logged in by clicking on the Profile icon in the top right corner and confirming your account details." />
        </ListItem>
        <ListItem>
          <ListItemText primary="3. Ensure the request method is set to GET." />
        </ListItem>
        <ListItem>
          <ListItemText primary={<span>4. In the URL field, enter the following address:</span>} />
        </ListItem>
        <ListItem>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              bgcolor: 'greyContrast',
              color: 'primary.contrastText',
              borderRadius: 1,
              border: '1px solid #ddd',
              p: 1,
              pl: 2,
            }}
          >
            <Box
              component="code"
              sx={{ fontSize: '0.9rem', display: 'block', wordBreak: 'break-all' }}
            >
              {getGraphExplorerUrl()}
            </Box>
            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
              <CopyButton textToCopy={getGraphExplorerUrl()} />
            </Box>
          </Box>
        </ListItem>
        <ListItem>
          <ListItemText primary='5. Click "Run query".' />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                6. In the response pane below, find and copy the <code>id</code> value. This is your
                Site ID.
              </span>
            }
          />
        </ListItem>
      </List>

      <TextField
        label="Site ID"
        value={siteId}
        onChange={handleSiteIdChange}
        onBlur={() => validateSiteId(siteId)}
        fullWidth
        required
        variant="outlined"
        margin="normal"
        sx={{ mt: 2 }}
        error={!!siteIdError}
        helperText={
          siteIdError ||
          'Example: yourco.sharepoint.com,12345678-abcd-1234-abcd-1234567890ab,etc...'
        }
      />
      <WizardNavigation
        activeStep={activeStep}
        onBack={onBack}
        onNext={handleNext}
        isNextDisabled={!siteId || !!siteIdError}
      />
    </Box>
  );
};

export default Step5SiteId;
